<template>
	<div class="eventsTimeline">
		<div v-if="!products.length && !events.length" class="text-ink-60">No events found.</div>

		<!-- See https://mobiscroll.com/docs/vue/eventcalendar/timeline#options for props descriptions -->
		<MbscEventcalendar
			v-else
			:click-to-create="clickToCreate"
			:drag-to-create="false"
			:drag-to-move="false"
			:drag-to-resize="false"
			:event-delete="false"
			:view="viewConfiguration"
			:data="visibleEvents"
			:drag-between-resources="false"
			:drag-time-step="1440"
			group-by="date"
			:resources="products"
			:show-controls="false"
			:show-event-tooltip="false"
			:css-class="timelineCSSClass"
			immutable-data
			:height="calendarHeight"
			theme="saas-da-timeline"
			:colors="cellColors"
			:invalid="invalidDates"
			:selected-date="selectedDate"
			:ref-date="visibleRangeStartDate"
			:extend-default-event="extendDefaultNewEvent"
			:event-overlap="false"
			@event-create="onEventCreate"
			@event-create-failed="(event) => emit('event-create-failed', event)"
			@event-update="onEventUpdate"
		>
			<!--
				These config options are temporarily disabled till we agree in detail on how to use drag/move/resize events
				from UI/UX perspective: `drag-to-create`, `drag-to-move`, `drag-to-resize`
			-->
			<template #scheduleEvent="data">
				<TimelineEventItem
					:event="data"
					:readonly="readonly || noEventInteractions || (isLoadingSkuData && isLoadingSkuData(String(data.currentResource?.id)))"
					:simplified="resolution === 'month'"
					@click="emit('event-click', data)"
				/>
			</template>

			<template #resourceHeader
				><TimelineResourcesHeader
					v-if="!readonly"
					:total-count="numberOfSkus"
					:selected-count="numberOfSelectedSkus"
					@toggle="emit('toggle-all-products')"
			/></template>

			<template #resource="resource">
				<TimelineResource
					:readonly="readonly"
					:flat="useFlatResources"
					:resource="resource"
					:store="store"
					@toggle-collapsed="() => emit('toggle-product', resource)"
				/>
			</template>

			<template #day="{ date, isActive }">
				<div
					:class="[
						'mbsc-date-header-day-name',
						{
							'mbsc-date-header-day-name--today': isActive,
							'mbsc-date-header-day-name--past': isInPast(date),
							'mbsc-date-header-day-name--weekend': isWeekend(date)
						}
					]"
				>
					{{ date.getDate() }}
				</div>
			</template>

			<template v-if="shouldShowTimelineFooter" #dayFooter="{ date }">
				<div
					:class="[
						'mbsc-date-footer-day-name',
						{
							'mbsc-date-footer-day-name--today': isSameDate(new Date(), date),
							'mbsc-date-footer-day-name--past': isInPast(date),
							'mbsc-date-footer-day-name--weekend': isWeekend(date)
						}
					]"
				>
					{{ date.getDate() }}
				</div>
			</template>

			<template #week="{ startDate, endDate }">
				<div
					:class="[
						'mbsc-date-header-week-name',
						{
							'mbsc-date-header-week-name--past': isInPast(endDate)
						}
					]"
				>
					{{ startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }}&nbsp;-&nbsp;{{
						endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
					}}
				</div>
			</template>

			<template v-if="resolution === 'month'" #month="{ date, isActive }">
				<div
					:class="[
						'mbsc-date-header-month-name flex flex-col justify-between',
						{
							'mbsc-date-header-month-name--past': !isActive && isInPast(date)
						}
					]"
				>
					{{ date?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) }}

					<div v-if="isActive" class="font-caption relative mt-04 flex-1 text-ink-inverted-60">
						<span class="absolute bottom-0 bg-primary-100 px-01 py-02" :style="currentDayOfMonthStyle">{{ today.day }}</span>
					</div>
				</div>
			</template>

			<template v-if="resolution === 'month' && shouldShowTimelineFooter" #monthFooter="{ date, isActive }">
				<div
					:class="[
						'mbsc-date-footer-month-name',
						{
							'mbsc-date-footer-month-name--past': !isActive && isInPast(date)
						}
					]"
				>
					{{ date?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) }}
				</div>
			</template>

			<template #quarter="{ date }">
				<div
					:class="[
						'mbsc-date-header-quarter-name',
						{
							'mbsc-date-header-quarter-name--past': isInPast(date)
						}
					]"
				>
					Q{{ date.getMonth() / 3 + 1 }} {{ date.getFullYear() }}
				</div>
			</template>

			<template #year="{ date }">
				<div
					:class="[
						'mbsc-date-header-year-name',
						{
							'mbsc-date-header-year-name--past': isInPast(date)
						}
					]"
				>
					{{ date.getFullYear() }}
				</div>
			</template>
		</MbscEventcalendar>
	</div>
</template>

<script setup lang="ts">
import { SPECIAL_EVENT_TEMPLATE_ID, type SkuId, type StoreName } from '@bluebrick/discounts-service-module'
import { isSameDate } from '@bluebrick/saas-ui'
import {
	MbscEventcalendar,
	type MbscCalendarColor,
	type MbscCalendarEvent,
	type MbscCalendarEventData,
	type MbscEventCreateEvent,
	type MbscEventCreateFailedEvent,
	type MbscEventUpdateEvent,
	type MbscEventcalendarView,
	type MbscNewEventData,
	type MbscResource
} from '@mobiscroll/vue'
import type { IValidateProps } from '@mobiscroll/vue/dist/src/core/util/datetime.js'
import { useWindowSize } from '@vueuse/core'
import { type DateTime } from 'luxon'
import { computed, toRefs } from 'vue'
import { isDateTimeBefore, isInPast, isWeekend, now } from '@/common/dateUtils'
import TimelineEventItem from '@/features/events/components/timeline/TimelineEventItem.vue'
import TimelineResource from '@/features/events/components/timeline/TimelineResource.vue'
import TimelineResourcesHeader from '@/features/events/components/timeline/TimelineResourcesHeader.vue'
import { EventType, type ProductResource, type TimelineEvent } from '@/types'

const props = defineProps<{
	products: MbscResource[]
	events: TimelineEvent[]
	isDataOld?: boolean
	store?: StoreName
	selectedDate?: DateTime<true>
	visibleRangeStartDate?: DateTime<true>
	clickToCreate?: boolean | 'single' | 'double'
	readonly?: boolean
	useFlatResources?: boolean
	noEventInteractions?: boolean
	isLoadingSkuData?: (skuId: string) => boolean
	selectedSkuIds?: SkuId[]
	resolution?: 'day' | 'week' | 'month' | 'year' | 'quarter'
	timelineSize?: number
}>()

const emit = defineEmits<{
	'event-create': [event: MbscEventCreateEvent]
	'event-create-failed': [event: MbscEventCreateFailedEvent]
	'event-update': [event: MbscEventUpdateEvent]
	'event-click': [event: MbscCalendarEventData]
	'toggle-product': [product: ProductResource]
	'toggle-all-products': [void]
}>()

const { products, events, selectedSkuIds, readonly, noEventInteractions, isLoadingSkuData } = toRefs(props)

const today = now()

const boundaryEvents = computed(() =>
	events.value.reduce<{ first: TimelineEvent | null; last: TimelineEvent | null }>(
		(acc, event) => {
			if (
				!acc.first?.rawEvent.start_date.isValid ||
				(event.rawEvent.start_date.isValid && isDateTimeBefore(event.rawEvent.start_date, acc.first.rawEvent.start_date))
			) {
				acc.first = event
			}

			if (
				!acc.last?.rawEvent.end_date.isValid ||
				(event.rawEvent.end_date.isValid && isDateTimeBefore(acc.last.rawEvent.end_date, event.rawEvent.end_date))
			) {
				acc.last = event
			}

			return acc
		},
		{ first: null, last: null }
	)
)
const firstEvent = computed(() => boundaryEvents.value.first)
const lastEvent = computed(() => boundaryEvents.value.last)

const visibleEvents = computed(() => events.value.filter((event) => !event.rawEvent.hidden))

const timelineSize = computed(() => {
	if (props.timelineSize) {
		return props.timelineSize
	}

	if (!firstEvent.value || !lastEvent.value) {
		return 1
	}

	switch (timelineUnit.value) {
		case 'day':
			return (
				Math.abs(Math.ceil(firstEvent.value.rawEvent.start_date.startOf('day').diff(lastEvent.value.rawEvent.end_date.endOf('day')).as('days'))) +
				1
			)

		case 'month':
			return (
				Math.abs(
					Math.ceil(firstEvent.value.rawEvent.start_date.startOf('day').diff(lastEvent.value.rawEvent.end_date.endOf('day')).as('months'))
				) + 1
			)

		default:
			return 1
	}
})

const timelineUnit = computed(() => {
	if (props.timelineSize || resolution.value === 'month') {
		return 'month'
	}

	return 'day'
})

const selectedDate = computed(() => {
	if (props.selectedDate) {
		return props.selectedDate
	}

	if (!firstEvent.value) {
		return today
	}

	return firstEvent.value.rawEvent.start_date
})

const visibleRangeStartDate = computed(() => {
	if (props.visibleRangeStartDate) {
		return props.visibleRangeStartDate
	}

	if (!firstEvent.value) {
		return today
	}

	return firstEvent.value.rawEvent.start_date
})

const resolution = computed(() => props.resolution ?? 'day')

const store = computed(() => {
	if (props.store) {
		return props.store
	}

	if (!firstEvent.value?.rawEvent.storeName) {
		// This will never happen anyway
		return '' as StoreName
	}

	return firstEvent.value.rawEvent.storeName
})

const timelineCSSClass = computed(() => {
	const classes = ['scheduler']

	if (resolution.value === 'day') {
		classes.push('scheduler--withDayNumbers')
	}

	return classes.join(' ')
})

const viewConfiguration = computed<MbscEventcalendarView>(() => ({
	timeline: {
		type: timelineUnit.value,
		size: timelineSize.value,
		currentTimeIndicator: false,
		startDay: 1,
		endDay: 0,
		rowHeight: 'equal',
		resolutionHorizontal: resolution.value
	}
}))

// Number of all (favourite) skus in store
const numberOfSkus = computed(() =>
	products.value.reduce((sum, product) => {
		if (product.children && product.children.length > 0) {
			// If it's a parent (product) then add number of its children
			return sum + product.children.length
		}

		// If its not a parent then its just a SKU so we add it to the sum
		return product.id !== SPECIAL_EVENT_TEMPLATE_ID ? sum + 1 : sum
	}, 0)
)

const numberOfSelectedSkus = computed(() => selectedSkuIds.value?.length ?? 0)

// Number of products + number of SKUs in all EXPANDED products
const totalNumberOfRows = computed(() =>
	products.value.reduce((sum, product) => {
		if (product.collapsed) {
			// Count collapsed row without children
			return sum + 1
		}

		// Count all children of expanded row + product row itself
		return sum + (product.children?.length ?? 0) + 1
	}, 0)
)

// We account for 4px/0.25rem padding at the bottom of the timeline to be able to fit hover
// effects of all icons in product name section. Similar padding is also added to the
// `mbsc-timeline-grid-scroll` element. See #bottom-padding-of-timeline
const bottomPaddingOfTimelineInPixels = 4
const bottomPaddingOfTimelineInRems = computed(() => `${bottomPaddingOfTimelineInPixels / 16}rem`)

// Top and bottom padding of each row of timeline
const topBottomRowPadding = 2
// Cell height without padding
const cellHeight = 26
const singleRowHeight = cellHeight + 2 * topBottomRowPadding

const { height } = useWindowSize()

// We want the expanded timeline to take maximum space available, but so that the store container below is visible (spaceForTheNextContainer)
const maxNumberOfVisibleRows = computed(() => {
	const paddingTopBottom = 24 * 2
	const header = 48
	const containerHeader = 56
	const oldDataWarning = props.isDataOld ? 44 : 0
	const timelineNavigation = 180
	const spaceForTheNextContainer = 70
	const remainingSpace =
		height.value - (paddingTopBottom + header + containerHeader + oldDataWarning + timelineNavigation + spaceForTheNextContainer)

	const numberOfRows = remainingSpace / singleRowHeight

	return Math.floor(numberOfRows)
})

const calendarHeight = computed<`${number}px` | undefined>(() => {
	if (totalNumberOfRows.value <= maxNumberOfVisibleRows.value) {
		// Just occupy as much space as needed
		return
	}

	// Height of the timeline's header
	const headerHeight = 58
	const footerHeight = 34

	// If there is more products/skus than `maxNumberOfVisibleRows` then we show `maxNumberOfVisibleRows`
	// and some of the additional row to better indicate that there is more products/skus and that the list
	// can be scrolled vertically
	const additionalHalfRowHeight = singleRowHeight / 4

	return `${headerHeight + (shouldShowTimelineFooter.value ? footerHeight : 0) + maxNumberOfVisibleRows.value * singleRowHeight + additionalHalfRowHeight + bottomPaddingOfTimelineInPixels}px`
})

/**
 * List CSS classes which adjust colors and contents of cells. We use it to:
 * - add a highlight to the "today" day cells
 * - style the special_event_template resource (themed events)
 */
const cellColors = computed<MbscCalendarColor[]>(() => {
	const colors: MbscCalendarColor[] = [
		{
			date: today,
			cssClass: 'mbsc-timeline-day--today'
		}
	]

	const startDate = new Date()
	const endDate = new Date()

	startDate.setFullYear(startDate.getFullYear() - 2)
	endDate.setFullYear(endDate.getFullYear() + 2)

	colors.push({
		resource: SPECIAL_EVENT_TEMPLATE_ID,
		start: startDate,
		end: endDate,
		cssClass: 'mbsc-timeline-day--special_event_template'
	})

	return colors
})

const invalidDates = computed<IValidateProps[]>(() => {
	const dataRangeStartDate = firstEvent.value?.rawEvent?.start_date

	if (!dataRangeStartDate?.isValid) {
		return []
	}

	return [
		{
			allDay: true,
			start: dataRangeStartDate.toJSDate(),
			end: now().minus({ day: 1 }).toJSDate()
		}
	]
})

/**
 * Positions the current day number badge (in monthly resolution)
 * more or less in the correct place relative to current date
 */
const currentDayOfMonthStyle = computed(() => {
	const percentage = (100 * today.day) / today.daysInMonth

	return {
		left: `${percentage}%`,
		transform: `translate(-${percentage}%, 0)`
	}
})

/**
 * Adjusts the event created automatically by Mobiscroll on timeline click or drag. Adds some props
 * needed to correctly render the "draft" of new event on timeline
 */
function extendDefaultNewEvent(newEvent: MbscNewEventData): MbscCalendarEvent {
	return {
		...newEvent,
		rawEvent: {
			...newEvent,
			event_type: EventType.LocalDraft,
			storeName: store.value,
			name: 'Custom Event'
		}
	}
}

// show timeline footer if there are 5 or more products on the list
const shouldShowTimelineFooter = computed(() => totalNumberOfRows.value > 4)

/**
 * Important to return false here so that Mobiscroll won't create it's own timeline event instance
 */
function onEventCreate(event: MbscEventCreateEvent): false {
	emit('event-create', event)

	return false
}

/**
 * Triggered when user resizes and existing discount event.
 * Return false here to stop rest of event updating logic on Mobiscroll's side (because we don't need it)
 */
function onEventUpdate(event: MbscEventUpdateEvent): false {
	emit('event-update', event)

	return false
}
</script>

<style lang="scss">
@import '@mobiscroll/vue/dist/css/mobiscroll.scss';
@import '@bluebrick/saas-ui/saas-ui.css';

:root {
	--timeline-cell-height: 1.625rem;
	--timeline-cell-width: 1.25rem;
}

$border-thickness: 0.125rem;
$border-thickness-thin: 0.0625rem;
// For now we leave this custom color (#434d5e) as it is
$header-border-color: #434d5e;
$timeline-border-color: theme('colors.level.01');
$header-border: $border-thickness-thin solid $header-border-color;
$timeline-cell-border: $border-thickness solid $timeline-border-color;
$timeline-cell-border-thin: $border-thickness-thin solid $timeline-border-color;

.scheduler {
	.mbsc-timeline-header-column,
	.mbsc-timeline-column {
		width: var(--timeline-cell-width);
	}

	.mbsc-timeline-column {
		&:hover {
			background-color: theme('colors.level.03');
			cursor: pointer;
		}
	}

	.mbsc-calendar-wrapper,
	.mbsc-timeline-resources,
	.mbsc-timeline-resource-col,
	.mbsc-timeline-header-month-text,
	.mbsc-timeline-header-bg,
	.mbsc-timeline-resource-bg {
		@apply bg-level-01;
	}

	.mbsc-timeline-resource {
		overflow: visible;
	}

	.mbsc-timeline-resource-title {
		position: relative;
		padding: 0;
		overflow: hidden; /* avoid unnecessary scrollbars */
	}

	.mbsc-timeline-resource-header {
		padding: 0;
	}

	.mbsc-timeline-resource-header-cont {
		display: flex;
		align-items: center;
	}

	.mbsc-timeline-resource-col {
		width: 17.75rem;
	}

	.mbsc-timeline-row {
		height: calc(var(--timeline-cell-height) + 2 * $border-thickness);

		&.is-loading {
			opacity: 0.3;
			pointer-events: none;
			cursor: default;
		}
	}

	.mbsc-timeline-row,
	.mbsc-timeline-column,
	.mbsc-timeline-header-column,
	.mbsc-timeline-header-date,
	.mbsc-timeline-footer-date,
	.mbsc-timeline-footer-week,
	.mbsc-timeline-resource-header-cont,
	.mbsc-timeline-resource-footer-cont,
	.mbsc-timeline-header-month,
	.mbsc-timeline-header-week,
	.mbsc-timeline-day-border::after {
		border: 0;
	}

	.mbsc-timeline-column {
		border-right: $timeline-cell-border-thin;
	}

	.mbsc-timeline-row-gutter {
		height: 0;
	}

	.mbsc-timeline-events {
		bottom: 0;
	}

	.mbsc-timeline-event {
		padding: 2px 0;
	}

	.mbsc-timeline-resources,
	.mbsc-timeline-resource-header-cont {
		border-right: $header-border;
	}

	.mbsc-date-header-day-name,
	.mbsc-date-footer-day-name {
		@apply font-caption-medium;
	}

	.mbsc-timeline-header-date,
	.mbsc-timeline-footer-date-cont,
	.mbsc-date-footer-day-name,
	.mbsc-date-footer-month-name,
	.mbsc-timeline-footer-week {
		height: var(--timeline-cell-height);
	}

	.mbsc-timeline-header-date,
	.mbsc-timeline-footer-bg {
		background-color: theme('colors.level.01');
	}

	.mbsc-date-header-day-name,
	.mbsc-date-footer-day-name {
		@apply flex items-center justify-center;
	}

	.mbsc-timeline-header {
		.mbsc-timeline-day-month,
		.mbsc-timeline-header-date,
		.mbsc-timeline-header-date-cont,
		.mbsc-date-header-day-name {
			height: var(--timeline-cell-height);
		}

		.mbsc-date-header-week-name,
		.mbsc-date-header-month-name,
		.mbsc-date-header-quarter-name,
		.mbsc-date-header-year-name {
			height: calc(2 * var(--timeline-cell-height));
		}

		.mbsc-date-header-day-name {
			@apply flex items-center justify-center;
		}

		.mbsc-timeline-day-border::after {
			border-right: $header-border;
		}

		.mbsc-timeline-header-week {
			border-right: $header-border;
		}
	}

	.mbsc-timeline-footer-week {
		border-right: $header-border;
		display: flex;
		align-items: center;
	}

	.mbsc-date-footer-month-name {
		display: flex;
		align-items: center;
	}

	.mbsc-timeline-footer-bg {
		// A hack to keep the footer at the bottom of the timeline and not 4px above it
		top: 0.25rem;
	}

	.mbsc-timeline-resource-footer {
		height: calc(var(--timeline-cell-height) + 2 * $border-thickness);
	}

	.mbsc-timeline-grid {
		.mbsc-timeline-row {
			@apply bg-level-02;
		}
	}

	.mbsc-timeline-header-sticky {
		@apply shadow-1;
	}

	.mbsc-date-header-day-name,
	.mbsc-date-footer-day-name {
		@apply text-ink-38;

		&--weekend {
			@apply text-ink-87;
		}

		&--today {
			@apply bg-primary-100 text-ink-inverted-87;
			// this is to prevent footer issue with today being visible under resource footer
			border-bottom: 0.0625rem solid theme('colors.level.01');
		}

		&--past {
			opacity: 0.35;
		}
	}

	.mbsc-date-header-week-name,
	.mbsc-date-header-month-name,
	.mbsc-date-footer-month-name,
	.mbsc-date-header-quarter-name,
	.mbsc-date-header-year-name {
		@apply font-caption-medium pl-1 text-ink-100;

		&--past {
			opacity: 0.35;
		}
	}

	.mbsc-timeline-header-month-text {
		@apply font-caption-medium pl-2 text-ink-100;
	}

	.mbsc-timeline-header-week-text {
		width: 100%;
	}

	.mbsc-timeline-day--today {
		&:after {
			@apply bg-primary-100;

			width: 1px;
			height: 100%;
			position: absolute;
			left: calc(50% - 1px);
			top: 0;
			content: '';
		}
	}

	.mbsc-timeline-day--special_event_template {
		background-color: theme('colors.level.01');
	}

	/*
	Ignore pointer events (cursor change, background color change, etc.) for every timeline cell
	(.mbsc-timeline-column) which belongs to a row (.mbsc-timeline-row) which has a special event
	(.mbsc-timeline-day--special_event_template)
	*/
	.mbsc-timeline-row:has(.mbsc-timeline-day--special_event_template) .mbsc-timeline-column {
		pointer-events: none;
	}

	.mbsc-timeline-grid-scroll {
		/* See #bottom-padding-of-timeline */
		padding-bottom: v-bind(bottomPaddingOfTimelineInRems);
	}

	.mbsc-calendar-custom-label {
		height: var(--timeline-cell-height);
	}

	.mbsc-calendar-row {
		border-right: $header-border;
		border-bottom: $header-border;
	}

	.mbsc-calendar-cell {
		border-top: $header-border;
		border-left: $header-border;
	}

	.mbsc-schedule-event-inactive {
		/* For some reasons timeline events have this class attached which by default changes the opacity to 0.5
		It wasn't like this before. TODO: investigate */
		opacity: 1;
	}

	.mbsc-timeline-resource {
		/* Remove the left padding which is used to show default expand-collapse icon
		in case of hierarchy of resources. We draw our own icon in TimelineResource.vue */
		padding-left: 0 !important; /* need to override inline padding set by Mobi */
	}

	.mbsc-timeline-resource-icon {
		/* Hide the default expand-collapse icon in case of hierarchy of resources.
		We draw our own icon in TimelineResource.vue */
		display: none;
		margin: 0;
	}

	.mbsc-timeline-parent {
		.mbsc-timeline-resource-title {
			/* Make sure that parent product/resource name occupies full height of timeline row */
			height: 2rem;
		}

		.mbsc-timeline-events,
		.mbsc-timeline-day {
			/* Hide timeline cell contents for parent product/resource rows */
			display: none;
			pointer-events: none;
		}

		&.mbsc-timeline-row {
			/* Render parent product/resource rows a bit brighter than actual SKU rows */
			background-color: theme('colors.level.01');
		}
	}

	.mbsc-timeline-row {
		.mbsc-timeline-day {
			/* Render the top border on specific day cells instead of entire resource row */
			border-top: $timeline-cell-border;
		}
	}

	::-webkit-scrollbar:horizontal {
		height: 1rem !important;
	}

	::-webkit-scrollbar:vertical {
		width: 1rem !important;
	}

	::-webkit-scrollbar-thumb:horizontal {
		border-top: 0.5rem solid theme('colors.level.01');
	}

	::-webkit-scrollbar-thumb:vertical {
		border-left: 0.5rem solid theme('colors.level.01');
	}
}
</style>
